/**
 * scopeCustomCss
 *
 * Takes raw CSS text (as entered in an admin's Custom CSS field) and rewrites
 * every selector so it only applies inside a given wrapper element, instead
 * of leaking out and affecting the rest of the site.
 *
 * Uses the browser's own CSS parser (via a detached <style> tag + CSSOM)
 * rather than regex, so nested at-rules like @media and @supports are
 * handled correctly instead of just pattern-matching selector text.
 *
 * Example:
 *   scopeCustomCss('.title { color: red; }', 'home-page-content')
 *   -> '#home-page-content .title { color: red; }'
 */
export function scopeCustomCss(rawCss: string | null | undefined, wrapperId: string): string {
    if (!rawCss || !rawCss.trim()) return "";

    // Callers pass either "id" or "#id" — normalize so we never emit "##id".
    const scopePrefix = `#${wrapperId.replace(/^#+/, "")}`;

    // Parse via a detached <style> tag so we get a real CSSOM to walk,
    // without ever inserting the raw (unscoped) rules into the visible page.
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-scope-parser", "true");
    styleEl.textContent = rawCss;
    document.head.appendChild(styleEl);

    let scoped = "";
    try {
        const sheet = styleEl.sheet as CSSStyleSheet | null;
        if (sheet) {
            scoped = Array.from(sheet.cssRules)
                .map((rule) => scopeRule(rule, scopePrefix))
                .join("\n");
        }
    } catch (e) {
        // Malformed CSS shouldn't break the page — just drop it.
        console.warn("scopeCustomCss: failed to parse custom CSS, ignoring it.", e);
        scoped = "";
    } finally {
        document.head.removeChild(styleEl);
    }

    return scoped;
}

function scopeRule(rule: CSSRule, scopePrefix: string): string {
    if (rule instanceof CSSStyleRule) {
        const scopedSelectors = rule.selectorText
            .split(",")
            .map((sel) => scopeSelector(sel.trim(), scopePrefix))
            .join(", ");
        return `${scopedSelectors} { ${rule.style.cssText} }`;
    }

    if (rule instanceof CSSMediaRule) {
        const inner = Array.from(rule.cssRules)
            .map((r) => scopeRule(r, scopePrefix))
            .join("\n");
        return `@media ${rule.conditionText} {\n${inner}\n}`;
    }

    if (typeof CSSSupportsRule !== "undefined" && rule instanceof CSSSupportsRule) {
        const inner = Array.from(rule.cssRules)
            .map((r) => scopeRule(r, scopePrefix))
            .join("\n");
        return `@supports ${rule.conditionText} {\n${inner}\n}`;
    }

    // @keyframes and other at-rules: leave untouched. Scoping keyframe names
    // would break animation-name references elsewhere in the same CSS block.
    return rule.cssText;
}

function scopeSelector(selector: string, scopePrefix: string): string {
    if (selector.startsWith(scopePrefix)) return selector;
    if (selector === "html" || selector === "body" || selector === ":root") {
        return scopePrefix;
    }
    return `${scopePrefix} ${selector}`;
}
